import { useEffect, useState } from 'react'
import { Typography, DatePicker, Row, Col, Select, Spin } from 'antd'
import { UserOutlined, CarOutlined, DollarOutlined, FileTextOutlined, TeamOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { getDashboardData } from '../actions/dashboardAction'
import Colors from '../utils/colors'
import dayjs from 'dayjs'
import { formatAmount } from '../helpers/Amount'

const { Text } = Typography

const Home = () => {
  const dispatch = useDispatch()
  const { loading, data } = useSelector((state) => state.dashboard)

  const MetricCard = ({ title, value, icon, color, pattern = 'default' }) => {
    const getBackgroundPattern = () => {
      switch(pattern) {
        case 'circles':
          return (
            <>
              <div style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: `${color}15`,
                border: `2px solid ${color}25`
              }} />
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                background: `${color}20`
              }} />
            </>
          )
        case 'waves':
          return (
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '60px',
              background: `linear-gradient(45deg, ${color}15 25%, transparent 25%), linear-gradient(-45deg, ${color}15 25%, transparent 25%)`,
              backgroundSize: '20px 20px'
            }} />
          )
        case 'diagonal':
          return (
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '0',
              height: '0',
              borderLeft: '80px solid transparent',
              borderTop: `80px solid ${color}20`
            }} />
          )
        case 'dots':
          return (
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '60px',
              height: '60px',
              background: `radial-gradient(circle, ${color}25 2px, transparent 2px)`,
              backgroundSize: '12px 12px'
            }} />
          )
        default:
          return (
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '80px',
              height: '80px',
              background: `linear-gradient(135deg, ${color}20, ${color}10)`,
              borderRadius: '0 16px 0 80px'
            }} />
          )
      }
    }
    
    return (
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #f0f0f0',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)'
        e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0px)'
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '80px',
          height: '80px',
          background: `linear-gradient(135deg, ${color}20, ${color}10)`,
          borderRadius: '0 16px 0 80px'
        }}>
        {getBackgroundPattern()}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: `linear-gradient(135deg, ${color}, ${color}dd)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '24px',
            flexShrink: 0
          }}>
            {icon}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: '4px' }}>
              <Text style={{ color: '#8c8c8c', fontSize: '14px', fontWeight: '500' }}>{title}</Text>
            </div>
            <div style={{ fontSize: 24, fontWeight: '700', color: '#262626', lineHeight: 1 }}>
              {value}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const rangeOptions = [
    { label: 'Last 1 Day', value: 0 },
    { label: 'Last 5 Days', value: 4 },
    { label: 'Last 7 Days', value: 6 },
    { label: 'Last 30 Days', value: 29 },
  ]

  const [startDate, setStartDate] = useState(dayjs().subtract(1, 'month'))
  const [endDate, setEndDate] = useState(dayjs())
  const [selectedRange, setSelectedRange] = useState(29)

  useEffect(() => {
    if (startDate && endDate) {
      dispatch(getDashboardData({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD')
      }))
    }
  }, [dispatch, startDate, endDate])

  const handleDateChange = (dates, field) => {
    if (field === 'start') {
      setStartDate(dates || dayjs())
    } else {
      setEndDate(dates || dayjs())
    }
  }

  const handleRangeChange = (value) => {
    setSelectedRange(value)
    const newEndDate = dayjs()
    const newStartDate = dayjs().subtract(value, 'day')
    setStartDate(newStartDate)
    setEndDate(newEndDate)
  }

  return (
    <Spin spinning={loading}>
      <div>
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6} lg={4}>
          <div style={{ marginBottom: '8px' }}>
            <Text strong>Date Range</Text>
          </div>
          <Select
            value={selectedRange}
            onChange={handleRangeChange}
            style={{ width: '100%' }}
            options={rangeOptions}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <div style={{ marginBottom: '8px' }}>
            <Text strong>Start Date</Text>
          </div>
          <DatePicker
            value={startDate}
            onChange={(date) => handleDateChange(date, 'start')}
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <div style={{ marginBottom: '8px' }}>
            <Text strong>End Date</Text>
          </div>
          <DatePicker
            value={endDate}
            onChange={(date) => handleDateChange(date, 'end')}
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
          />
        </Col>
      </Row>
      
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <MetricCard
            title="Total Orders"
            value={data?.counts?.totalOrders || 0}
            icon={<FileTextOutlined />}
            color={Colors.blue}
            pattern="circles"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <MetricCard
            title="Total Amount"
            value={formatAmount(data?.counts?.totalAmount || 0)}
            icon={<DollarOutlined />}
            color={Colors.success}
            pattern="waves"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <MetricCard
            title="Total Commission"
            value={formatAmount(data?.counts?.totalCommission || 0)}
            icon={<DollarOutlined />}
            color={Colors.purple}
            pattern="diagonal"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <MetricCard
            title="Refunded Amount"
            value={formatAmount(data?.counts?.totalRefundedAmount || 0)}
            icon={<DollarOutlined />}
            color={Colors.red}
            pattern="dots"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <MetricCard
            title="Road Tax"
            value={data?.counts?.roadTax || 0}
            icon={<CarOutlined />}
            color={Colors.orange}
            pattern="circles"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <MetricCard
            title="Border Tax"
            value={data?.counts?.borderTax || 0}
            icon={<CarOutlined />}
            color={Colors.warning}
            pattern="waves"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <MetricCard
            title="All India Tax"
            value={data?.counts?.allIndiaTax || 0}
            icon={<CarOutlined />}
            color={Colors.cyan}
            pattern="diagonal"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <MetricCard
            title="All India Permit"
            value={data?.counts?.allIndiaPermit || 0}
            icon={<FileTextOutlined />}
            color={Colors.pink}
            pattern="dots"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <MetricCard
            title="Employees"
            value={data?.counts?.employees || 0}
            icon={<TeamOutlined />}
            color={Colors.darkRed}
            pattern="circles"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <MetricCard
            title="Users"
            value={data?.counts?.users || 0}
            icon={<UserOutlined />}
            color={Colors.darkBlue}
            pattern="waves"
          />
        </Col>
      </Row>
      </div>
    </Spin>
  )
}

export default Home